import { useState } from 'react';
import { Lightbulb, Lock, X } from 'lucide-react';
import { Card } from './ui/card';

const STORAGE_KEY = 'lizrd.settings-guide.collapsed';

// Why we ask for integrations, and what the customer gets back for each one.
const POINTS = [
  {
    title: 'You connect your sources',
    body: 'Link the systems that shape this domain — your code on GitHub, your infrastructure on AWS, and more over time. Access is read-only, credentials are encrypted, and you can disconnect any source at any moment.',
  },
  {
    title: 'Lizrd builds the picture',
    body: 'We map how your code turns into running resources and how those resources turn into cost — one connected model of your domain, kept current on every sync.',
  },
  {
    title: 'Your guidance gets sharper',
    body: 'The more you connect, the more context Lizrd has to reason with — so its cost explanations get more precise and its optimization guidance more confident. Each integration compounds the last.',
  },
] as const;

/**
 * The Settings tab's trust box: explains why Lizrd asks for integrations, what
 * it does with them, and the flywheel — more connected sources means richer
 * reasoning and sharper optimization guidance. Collapsible, expanded on first
 * visit (persisted), so returning managers can tuck it away.
 */
export function IntegrationsGuide() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const persist = (value: boolean) => {
    setCollapsed(value);
    try {
      localStorage.setItem(STORAGE_KEY, value ? '1' : '0');
    } catch {
      /* private mode — the choice just won't persist */
    }
  };

  if (collapsed) {
    return (
      <button
        onClick={() => persist(false)}
        className="flex w-full items-center gap-2 rounded-md border border-hairline bg-surface-2/40 px-4 py-2 text-left text-xs transition-colors hover:bg-surface-2"
      >
        <Lightbulb aria-hidden className="h-4 w-4 text-iris" />
        <span className="font-semibold text-haze">Why connect your sources?</span>
        <span className="ml-auto text-muted">Show ▸</span>
      </button>
    );
  }

  return (
    <Card className="relative space-y-4 p-5">
      <button
        onClick={() => persist(true)}
        aria-label="Hide explanation"
        className="absolute right-3 top-3 grid h-6 w-6 place-items-center rounded-sm border border-border text-muted transition-colors hover:bg-surface-2 hover:text-bone"
      >
        <X aria-hidden className="h-4 w-4" />
      </button>

      <div className="space-y-1 pr-8">
        <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-iris">
          Why connect your sources
        </div>
        <h2 className="font-display text-lg font-bold text-bone">
          The More Lizrd Sees, the Sharper Its Guidance
        </h2>
        <p className="text-sm text-haze">
          Every source you connect flows into this domain and becomes part of how Lizrd reasons
          about your cost. Here’s what happens with the data you share.
        </p>
      </div>

      <ol className="grid gap-5 sm:grid-cols-3">
        {POINTS.map((point, i) => (
          <li key={point.title} className="flex h-full flex-col">
            <div className="font-mono text-2xl font-bold leading-none text-iris tnum">
              {String(i + 1).padStart(2, '0')}
            </div>
            <h3 className="mt-2.5 font-semibold text-bone">{point.title}</h3>
            <p className="mt-1.5 text-xs leading-snug text-muted">{point.body}</p>
          </li>
        ))}
      </ol>

      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-iris/25 bg-iris/5 px-3 py-2 text-sm">
        <Lock aria-hidden className="h-4 w-4 flex-none text-iris" />
        <span className="text-haze">
          Read-only access, encrypted credentials, disconnect anytime —{' '}
          <span className="text-bone">your data stays yours.</span>
        </span>
      </div>
    </Card>
  );
}
